import { HomePageSettings, sanitizeHomePage } from '../app/utils/homePageSettings';

/**
 * Spec 029 US4: the last home page answer, kept in this browser so the next
 * visit shows it at once while the API is asked again. Per brand; each site
 * already has its own origin, so staging and production never mix.
 */
export type CachedHomePage = {
  homeBannerImageKey: string | null;
  homePage: HomePageSettings;
  savedAt: number;
};

export function homePageCacheKey(brandSite: string): string {
  return `home_page_v1_${brandSite}`;
}

function safeLocalStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

export function readHomePageCache(
  brandSite: string,
  storage: Pick<Storage, 'getItem'> | null = safeLocalStorage(),
): CachedHomePage | null {
  try {
    const raw = storage?.getItem(homePageCacheKey(brandSite));
    if (!raw) return null;
    const value = JSON.parse(raw);
    if (!value || typeof value !== 'object') return null;
    const key = typeof value.homeBannerImageKey === 'string' ? value.homeBannerImageKey : null;
    return {
      homeBannerImageKey: key && key.trim() ? key : null,
      homePage: sanitizeHomePage(value.homePage),
      savedAt: typeof value.savedAt === 'number' ? value.savedAt : 0,
    };
  } catch {
    return null;
  }
}

export function writeHomePageCache(
  brandSite: string,
  value: { homeBannerImageKey?: string | null; homePage?: unknown },
  storage: Pick<Storage, 'setItem'> | null = safeLocalStorage(),
  now: number = Date.now(),
): void {
  try {
    const entry: CachedHomePage = {
      homeBannerImageKey: value.homeBannerImageKey ?? null,
      homePage: sanitizeHomePage(value.homePage),
      savedAt: now,
    };
    storage?.setItem(homePageCacheKey(brandSite), JSON.stringify(entry));
  } catch {
    // Private mode or a full quota: the page still works from the API.
  }
}
